import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, List, ListItem, ListItemText, Typography, Divider } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { type CSSObject, styled, useTheme, type Theme } from '@mui/material/styles';
import { RichTextReadOnly } from 'mui-tiptap';
import React, { type FC, useEffect, useMemo } from 'react';

import { type EmergencyContact } from '../../models/index.js';
import useExtensions from '../MUIEditor/useExtensions.js';


// const drawerWidth = 260;
const drawerWidth = 360;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: 0,
  border: 'none',
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: 0,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    width: 0,
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

interface CareInformationDrawerProps {
  open: boolean;
  onClose: () => void;
  careInstructions?: string;
  emergencyContacts?: EmergencyContact[];
  activeDrawerView: 'emergencyContacts' | 'careInformation';
}

const CareInformationDrawer: FC<CareInformationDrawerProps> = ({
  open,
  onClose,
  careInstructions,
  emergencyContacts,
  activeDrawerView,
}) => {
  const theme = useTheme();
  const extensions = useExtensions();
  const sortedEmergencyContacts = useMemo(
    () => emergencyContacts?.sort((a, b) => a.priority + b.priority) || [],
    [emergencyContacts],
  );
  useEffect(() => {
    return () => {
      onClose();
    };
  }, []);

  return (
    <Drawer anchor={'right'} open={open} onClose={onClose} variant={'permanent'}>
      <DrawerHeader>
        <IconButton onClick={onClose}>
          {theme.direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
        <Typography variant={'h5'} sx={{ fontWeight: 'bold', flexGrow: 1 }}>
          {activeDrawerView === 'emergencyContacts' ? 'Emergency Contacts' : 'Care Information'}
        </Typography>
      </DrawerHeader>
      <Divider />
      <Box sx={{ p: 2 }}>
        {activeDrawerView === 'emergencyContacts' ? (
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {sortedEmergencyContacts.map((emergencyContact) => (
              <ListItem key={emergencyContact.id}>
                <ListItemText
                  primary={`${emergencyContact.firstName} ${emergencyContact.lastName}`}
                  secondary={`${emergencyContact.relationship || 'Unknown'} | ${emergencyContact.phoneNumber}`}
                />
              </ListItem>
            ))}
          </List>
        ) : careInstructions ? (
          <RichTextReadOnly content={careInstructions} extensions={extensions} />
        ) : null}
      </Box>
    </Drawer>
  );
};

export default CareInformationDrawer;
